import { emitHintSignal } from '@flobi/asset-library/hints';
import { ALL_TOOLS } from '../../editor/shell/Toolbar';
import { keyBelongsToField, menuIsOpen, primaryKey, secondKey, typingIsOnCanvas } from '../../ui/keys';

/**
 * The shortcuts a hint teaches, heard as the editor hears them: someone who presses ⌘G or ⇧⌘E
 * already knows the key, so its hint never shows them (`learnedBy`); ⌘D is one more copy made
 * one at a time, which the Cloner hint counts. It listens beside the
 * editor's own handlers (EditorPage, useEditorCanvas) and skips the same presses they skip: an
 * open menu's keys, a field's keys, letters typed into text on the canvas.
 *
 * Only on a file (`/f/…`). Names never leave: each signal is a name and, at most, a flag.
 *
 * Returns how to stop listening.
 */
export function listenForShortcutSignals(target: Window = window): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (!e.isTrusted || e.repeat || menuIsOpen() || typingIsOnCanvas() || keyBelongsToField(e.target, e.key)) return;
    if (!/^\/f\//.test(target.location.pathname)) return;
    if (primaryKey(e)) return withCommand(e);
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    bare(e);
  };
  target.addEventListener('keydown', onKey, true);
  return () => target.removeEventListener('keydown', onKey, true);
}

/** Something is chosen: the quick bar stands over it (QuickBar.tsx), and ⌘D copies only then. */
const somethingChosen = () => document.querySelector('.selection-bar') !== null;

/** The Animate bar is out (AnimateBar.tsx), where a tap of Space plays and pauses. */
const animating = () => document.querySelector('section[aria-label="Animate"]') !== null;

function withCommand(e: KeyboardEvent) {
  const key = e.code === 'KeyD' ? 'd' : e.code === 'KeyG' ? 'g' : e.code === 'KeyE' ? 'e' : e.code === 'KeyS' ? 's' : e.key.toLowerCase();
  if (key === 'd' && !e.shiftKey && !e.altKey) {
    if (!somethingChosen()) return;
    emitHintSignal('layer.duplicated', { viaButton: false });
    emitHintSignal('shortcut.duplicate');
  } else if (key === 'g' && !e.altKey && !e.shiftKey) emitHintSignal('shortcut.group'); // ⇧⌘G takes a group apart
  else if (key === 'e' && e.shiftKey && !e.altKey) emitHintSignal('shortcut.export');
  else if (key === 's' && secondKey(e) && !e.shiftKey) emitHintSignal('shortcut.library');
  else if (key === 's' && e.shiftKey && !secondKey(e)) emitHintSignal('shortcut.share');
  else if (e.code === 'BracketRight' || e.code === 'BracketLeft') emitHintSignal('shortcut.arrange');
}

/** Every tool key the toolbar lists (P, M, R, ⇧C …) but Select's V, which has its own hint; L draws an ellipse. */
const TOOL_KEYS = new Set([...ALL_TOOLS.filter((mark) => mark.id !== 'select').map((mark) => (mark.shortcut ?? '').replace('⇧', '').toLowerCase()), 'l'].filter((key) => key.length === 1));

function bare(e: KeyboardEvent) {
  const letter = /^Key[A-Z]$/.test(e.code) ? e.code.slice(3).toLowerCase() : '';
  if (letter === 'v' && !e.shiftKey) emitHintSignal('shortcut.select');
  else if (letter && TOOL_KEYS.has(letter)) emitHintSignal('shortcut.tool');
  else if ((e.code === 'Digit0' || e.code === 'Digit1') && !e.shiftKey) emitHintSignal('shortcut.zoom');
  else if (e.shiftKey && /^Arrow(Left|Right|Up|Down)$/.test(e.key)) emitHintSignal('shortcut.bigNudge');
  else if (e.code === 'Space' && animating()) emitHintSignal('shortcut.animatePlay');
}
